/**
 * featureGating.js
 *
 * Central feature-gating configuration for Athletes Launch.
 * Maps subscription tiers to the features they unlock, and provides
 * helper functions to check access throughout the app.
 *
 * ─── Athlete tiers ───
 *   athlete_free   → Starter
 *   athlete_pro    → Pro
 *   athlete_elite  → Elite
 *
 * ─── Employer tiers ───
 *   employer_free       → Basic
 *   employer_business   → Business
 *   employer_enterprise → Enterprise
 */

// ─── Tier hierarchy (higher index = higher tier) ────────────────────────────
export const ATHLETE_TIER_ORDER = ['athlete_free', 'athlete_pro', 'athlete_elite'];
export const EMPLOYER_TIER_ORDER = ['employer_free', 'employer_business', 'employer_enterprise'];
export const COLLECTIVE_TIER_ORDER = ['collective_free', 'collective_growth', 'collective_premier'];
export const AGENCY_TIER_ORDER = ['agency_free', 'agency_professional', 'agency_enterprise'];

// ─── Tier display names ─────────────────────────────────────────────────────
export const TIER_LABELS = {
  athlete_free: 'Starter',
  athlete_pro: 'Pro',
  athlete_elite: 'Elite',
  employer_free: 'Basic',
  employer_business: 'Business',
  employer_enterprise: 'Enterprise',
  collective_free: 'Starter',
  collective_growth: 'Growth',
  collective_premier: 'Premier',
  agency_free: 'Starter',
  agency_enterprise: 'Enterprise',
};

// ─── Feature definitions ────────────────────────────────────────────────────
// Each feature has an id, a display name, and the minimum required tier for
// each user type (athlete / employer).  If a user type is omitted, that type
// does NOT have access to the feature at all.
export const FEATURES = {
  // ── Athlete features ──────────────────────────────────────
  browse_opportunities: {
    name: 'Browse Opportunities',
    athlete: 'athlete_free',      // all athletes
  },
  apply_opportunities: {
    name: 'Apply to Opportunities',
    athlete: 'athlete_free',
    // Starter: 3/month, Pro+: unlimited (enforced in UI)
  },
  view_media: {
    name: 'View Media',
    athlete: 'athlete_free',
    employer: 'employer_free',
  },
  view_events: {
    name: 'View Events',
    athlete: 'athlete_free',
    employer: 'employer_free',
  },

  unlimited_applications: {
    name: 'Unlimited Applications',
    athlete: 'athlete_pro',
  },
  featured_in_search: {
    name: 'Featured in Employer Search',
    athlete: 'athlete_pro',
  },
  priority_support: {
    name: 'Priority Support',
    athlete: 'athlete_pro',
  },
  auto_clip: {
    name: 'Auto Clip',
    athlete: 'athlete_pro',       // Pro and above
  },
  performance_analytics: {
    name: 'Performance Analytics',
    athlete: 'athlete_elite',
  },
  elite_badge: {
    name: 'Elite Profile Badge',
    athlete: 'athlete_elite',
  },
  top_search_placement: {
    name: 'Top Placement in Search Results',
    athlete: 'athlete_elite',
    employer: 'employer_enterprise',
  },
  unlimited_uploads: {
    name: 'Unlimited File Uploads',
    athlete: 'athlete_elite',
  },
  direct_introductions: {
    name: 'Direct Company Introductions',
    athlete: 'athlete_elite',
  },

  // ── Employer features ─────────────────────────────────────
  browse_athletes: {
    name: 'Browse Athletes',
    employer: 'employer_free',    // all employers
  },
  post_opportunity: {
    name: 'Post Opportunity',
    employer: 'employer_free',
    // Basic: 1 active, Business: 10, Enterprise: unlimited
  },
  post_media: {
    name: 'Post Media',
    employer: 'employer_business',
  },
  post_event: {
    name: 'Post Events',
    employer: 'employer_business',
  },
  advanced_search_filters: {
    name: 'Advanced Athlete Search Filters',
    employer: 'employer_business',
  },
  athlete_contact_info: {
    name: 'Access Athlete Contact Info',
    employer: 'employer_business',
  },
  bookmark_candidates: {
    name: 'Bookmark & Track Candidates',
    employer: 'employer_business',
  },
  unlimited_postings: {
    name: 'Unlimited Opportunity Postings',
    employer: 'employer_enterprise',
  },
  featured_employer_badge: {
    name: 'Featured Employer Badge',
    employer: 'employer_enterprise',
  },
  dedicated_account_manager: {
    name: 'Dedicated Account Manager',
    employer: 'employer_enterprise',
  },

  // ── NIL Marketplace features ──────────────────────────────────
  nil_browse_deals: {
    name: 'Browse NIL Deals',
    athlete: 'athlete_free',
    employer: 'employer_free',
  },
  nil_connect: {
    name: 'Send NIL Connection Requests',
    athlete: 'athlete_free',
    // Free: 3/month, Pro+: unlimited (enforced in UI)
  },
  nil_unlimited_connections: {
    name: 'Unlimited NIL Connections',
    athlete: 'athlete_pro',
  },
  nil_featured_profile: {
    name: 'Featured in NIL Search',
    athlete: 'athlete_pro',
  },
  nil_analytics: {
    name: 'NIL Deal Analytics',
    athlete: 'athlete_pro',
  },
  nil_priority_placement: {
    name: 'Top NIL Search Placement',
    athlete: 'athlete_elite',
  },
  nil_verified_badge: {
    name: 'NIL Verified Badge',
    athlete: 'athlete_elite',
  },
  nil_direct_intros: {
    name: 'Direct Company Introductions (NIL)',
    athlete: 'athlete_elite',
  },
  nil_post_deal: {
    name: 'Post NIL Deals',
    employer: 'employer_free',
    // Free: 1, Business: 10, Enterprise: unlimited
  },
  nil_athlete_search: {
    name: 'Search Athletes for NIL',
    employer: 'employer_business',
  },
  nil_athlete_contact: {
    name: 'Access Athlete NIL Contact Info',
    employer: 'employer_business',
  },
  nil_unlimited_deals: {
    name: 'Unlimited NIL Deal Postings',
    employer: 'employer_enterprise',
  },
  nil_featured_company: {
    name: 'Featured Company in NIL Marketplace',
    employer: 'employer_enterprise',
    collective: 'collective_premier',
    agency: 'agency_enterprise',
  },

  // ── Collective features ────────────────────────────────────
  browse_athletes_collective: {
    name: 'Browse Athletes',
    collective: 'collective_free',
  },
  post_opportunity_collective: {
    name: 'Post Opportunity',
    collective: 'collective_free',
  },
  post_media_collective: {
    name: 'Post Media',
    collective: 'collective_growth',
  },
  post_event_collective: {
    name: 'Post Events',
    collective: 'collective_growth',
  },

  // ── Agency features ────────────────────────────────────────
  browse_athletes_agency: {
    name: 'Browse Athletes',
    agency: 'agency_free',
  },
  post_opportunity_agency: {
    name: 'Post Opportunity',
    agency: 'agency_free',
  },
  post_media_agency: {
    name: 'Post Media',
    agency: 'agency_enterprise', // remapped from a retired mid-ladder tier, see note below
  },
  post_event_agency: {
    name: 'Post Events',
    agency: 'agency_enterprise', // remapped from a retired mid-ladder tier
  },

  // ── Team Management features ────────────────────────────────
  manage_team_members: {
    name: 'Manage Team Members',
    employer: 'employer_business',
    collective: 'collective_growth',
    agency: 'agency_professional', // remapped from the retired ladder's first paid tier
  },
  team_outreach: {
    name: 'Outreach on Behalf of Organization',
    employer: 'employer_free',
    collective: 'collective_free',
    agency: 'agency_free',
  },
  // ?? Agency-specific features ????????????????????????????????
  // NOTE on the thresholds below: these features were originally gated at
  // various rungs of a retired 7-tier agency ladder (free / first-paid $99 /
  // then $249, $549, $999, $1799 / top $3499). The live registry only has
  // 3 agency tiers (free / agency_professional $59.99 / agency_enterprise
  // $149.99). Remapped by price-point correspondence: the retired ladder's
  // first paid tier -> agency_professional (first paid tier -> first paid
  // tier); every higher retired tier (all priced above the new
  // agency_enterprise) -> agency_enterprise, since that never grants access
  // more broadly than the original tier's intent. See PLAN-A follow-up:
  // PricingPage.js still needs a real product decision to replace this
  // ladder with actual 2026 agency-tier feature packaging.
  agency_csv_import: {
    name: 'CSV Bulk Athlete Import',
    agency: 'agency_professional', // remapped from the retired ladder's first paid tier
  },
  agency_branded_profiles: {
    name: 'Branded Athlete Profiles',
    agency: 'agency_enterprise', // remapped from a retired mid-ladder tier
  },
  agency_custom_subdomain: {
    name: 'Custom Subdomain',
    agency: 'agency_enterprise', // remapped from a retired mid-ladder tier
  },
  agency_full_analytics: {
    name: 'Full Agency Analytics Dashboard',
    agency: 'agency_enterprise', // remapped from a retired mid-ladder tier
  },
  agency_white_label: {
    name: 'Full White-Label Branding',
    agency: 'agency_enterprise', // remapped from a retired mid-ladder tier
  },
  agency_custom_domain: {
    name: 'Custom Domain (yourplatform.com)',
    agency: 'agency_enterprise', // remapped from a retired mid-ladder tier
  },
  agency_white_label_email: {
    name: 'White-Label Email',
    agency: 'agency_enterprise', // remapped from a retired mid-ladder tier
  },
  agency_api_access: {
    name: 'API Access',
    agency: 'agency_enterprise', // remapped from a retired mid-ladder tier
  },
  agency_commission_dashboard: {
    name: 'Revenue & Commission Dashboard',
    agency: 'agency_enterprise', // remapped from a retired mid-ladder tier
  },
  agency_mobile_app: {
    name: 'Custom Mobile App (iOS + Android)',
    agency: 'agency_enterprise', // remapped from the retired ladder's top tier
  },
  agency_automated_payouts: {
    name: 'Automated Commission Payouts',
    agency: 'agency_enterprise', // remapped from the retired ladder's top tier
  },

  // ── Blast (Cross-Platform Content Syndication) ────────────
  blast: {
    name: 'Blast Content Syndicator',
    athlete: 'athlete_pro',        // Pro and above
    employer: 'employer_business', // Business and above
    collective: 'collective_growth',
    agency: 'agency_professional', // remapped from the retired ladder's first paid tier
  },
};

// ─── Tier limit configuration ────────────────────────────────────────────────
// Numeric limits per tier (e.g. max applications, max uploads, max postings)
export const TIER_LIMITS = {
  athlete_free: {
    maxApplicationsPerMonth: 10,
    maxResumes: 1,
    maxNILConnectionsPerMonth: 3,
    maxBlastsPerMonth: 0,
  },
  athlete_pro: {
    maxApplicationsPerMonth: Infinity,
    maxResumes: 5,
    maxNILConnectionsPerMonth: Infinity,
    maxBlastsPerMonth: 15,
  },
  athlete_elite: {
    maxApplicationsPerMonth: Infinity,
    maxResumes: Infinity,
    maxNILConnectionsPerMonth: Infinity,
    maxBlastsPerMonth: Infinity,
  },
  employer_free: {
    maxActivePostings: 1,
    maxActiveNILDeals: 1,
    maxBlastsPerMonth: 0,
  },
  employer_business: {
    maxActivePostings: 10,
    maxActiveNILDeals: 10,
    maxTeamMembers: 5,
    maxBlastsPerMonth: 20,
  },
  employer_enterprise: {
    maxActivePostings: Infinity,
    maxActiveNILDeals: Infinity,
    maxTeamMembers: Infinity,
    maxBlastsPerMonth: Infinity,
  },
  collective_free: {
    maxActivePostings: 1,
    maxActiveNILDeals: 1,
  },
  collective_growth: {
    maxActivePostings: 10,
    maxActiveNILDeals: 10,
    maxTeamMembers: 5,
  },
  collective_premier: {
    maxActivePostings: Infinity,
    maxActiveNILDeals: Infinity,
    maxTeamMembers: Infinity,
  },
  agency_free: {
    maxAthletes:       5,
    maxActivePostings: 1,
    maxActiveNILDeals: 1,
    maxTeamMembers:    1,
  },
  agency_enterprise: {
    maxAthletes:       400,
    maxActivePostings: Infinity,
    maxActiveNILDeals: Infinity,
    maxTeamMembers:    Infinity,
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Detect the user type prefix and return the matching tier order.
 */
function getTierOrder(tier) {
  if (tier.startsWith('athlete')) return ATHLETE_TIER_ORDER;
  if (tier.startsWith('employer')) return EMPLOYER_TIER_ORDER;
  if (tier.startsWith('collective')) return COLLECTIVE_TIER_ORDER;
  if (tier.startsWith('agency')) return AGENCY_TIER_ORDER;
  return [];
}

/**
 * Returns the user-type key ('athlete', 'employer', 'collective', 'agency')
 * from a tier id.
 */
function getUserTypeFromTier(tier) {
  if (!tier) return null;
  return tier.split('_')[0];
}

/**
 * Returns whether a given user tier is at or above the required tier.
 * Only compares within the same user type.
 */
export function isTierAtLeast(userTier, requiredTier) {
  if (!userTier || !requiredTier) return false;

  const userType = getUserTypeFromTier(userTier);
  const reqType = getUserTypeFromTier(requiredTier);

  // Different user types can't be compared
  if (userType !== reqType) return false;

  const order = getTierOrder(userTier);
  const reqIndex = order.indexOf(requiredTier);
  if (reqIndex === -1) {
    console.warn(`[featureGating] unknown required tier "${requiredTier}" — denying access`);
    return false;
  }
  return order.indexOf(userTier) >= reqIndex;
}

/**
 * Checks whether a user with the given tier has access to a specific feature.
 *
 * @param {string} userTier  – e.g. 'athlete_pro' or 'employer_business'
 * @param {string} featureId – key from the FEATURES map
 * @returns {boolean}
 */
export function hasFeatureAccess(userTier, featureId) {
  const feature = FEATURES[featureId];
  if (!feature) {
    console.warn(`featureGating: unknown feature "${featureId}"`);
    return false;
  }

  if (!userTier) return false;

  const userType = getUserTypeFromTier(userTier);
  const requiredTier = feature[userType];

  if (!requiredTier) return false; // feature not available for this user type

  return isTierAtLeast(userTier, requiredTier);
}

/**
 * Returns the minimum tier required for a feature, given the user type.
 * Returns null if the feature is not available for that user type.
 */
export function getRequiredTier(featureId, userType) {
  const feature = FEATURES[featureId];
  if (!feature) return null;
  return feature[userType] || null;
}

/**
 * Returns the numeric limits for a tier.
 */
export function getLimitsForTier(tier) {
  return TIER_LIMITS[tier] || {};
}

/**
 * Returns the display label for a tier id.
 */
export function getTierLabel(tierId) {
  return TIER_LABELS[tierId] || tierId || 'Free';
}

/**
 * Returns the minimum tier id that would unlock a feature
 * (for upgrade prompt messages).
 */
export function getMinimumTierForFeature(featureId, userType) {
  const feature = FEATURES[featureId];
  if (!feature) return null;
  return feature[userType] || null;
}
